import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import TopBar from "@/components/dashboard/TopBar";
import FiltersSidebar from "@/components/dashboard/FiltersSidebar";
import ConversationTable from "@/components/dashboard/ConversationTable";
import DetailDrawer from "@/components/dashboard/DetailDrawer";
import ApiKeyDialog from "@/components/dashboard/ApiKeyDialog";
import SyncLogsDialog from "@/components/dashboard/SyncLogsDialog";
import UsersTable from "@/components/dashboard/UsersTable";
import UserDetailDrawer from "@/components/dashboard/UserDetailDrawer";
import {
  clearStoredPasscode,
  getApiKeyStatus,
  getCreditStats,
  getFacets,
  getSyncStatus,
  listConversations,
  startSync,
  buildCsvUrl,
} from "@/lib/api";

const emptyFilters = {
  q: "",
  status: "",
  call_successful: "",
  environment: "",
  month: "",
  archived: "",
  date_from: "",
  date_to: "",
  duration_min: "",
  duration_max: "",
  phone: "",
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const [apiKeyState, setApiKeyState] = useState({ connected: false });
  const [apiKeyOpen, setApiKeyOpen] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [syncState, setSyncState] = useState({ running: false, latest: null, total_conversations: 0 });
  const [creditStats, setCreditStats] = useState(null);
  const [facets, setFacets] = useState({ environments: [], statuses: [], call_successful: [], agents: [] });
  const [filters, setFilters] = useState(emptyFilters);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activeTab, setActiveTab] = useState("conversations");
  const [selectedUser, setSelectedUser] = useState(null);
  const pollRef = useRef(null);

  const filterParams = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) p[k] = v;
    });
    if (p.date_from) p.date_from = Math.floor(new Date(p.date_from).getTime() / 1000);
    if (p.date_to) p.date_to = Math.floor(new Date(p.date_to).getTime() / 1000);
    return p;
  }, [filters]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listConversations({ ...filterParams, page, page_size: pageSize });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      if (e?.response?.status === 401) {
        clearStoredPasscode();
        navigate("/");
        return;
      }
      toast.error("Failed to load conversations");
    } finally {
      setLoading(false);
    }
  }, [filterParams, page, pageSize, navigate]);

  const refreshFacets = useCallback(async () => {
    try {
      setFacets(await getFacets());
    } catch {
      /* facets optional */
    }
  }, []);

  const refreshSync = useCallback(async () => {
    try {
      const s = await getSyncStatus();
      setSyncState(s);
      return s;
    } catch {
      return null;
    }
  }, []);

  const refreshCredits = useCallback(async () => {
    try {
      const c = await getCreditStats();
      setCreditStats(c);
      return c;
    } catch {
      return null;
    }
  }, []);

  const refreshApiKey = useCallback(async () => {
    try {
      const s = await getApiKeyStatus();
      setApiKeyState(s);
      if (!s.connected) setApiKeyOpen(true);
      return s;
    } catch (e) {
      if (e?.response?.status === 401) {
        clearStoredPasscode();
        navigate("/");
      }
      return { connected: false };
    }
  }, [navigate]);

  useEffect(() => {
    refreshApiKey();
    refreshSync();
    refreshFacets();
    refreshCredits();
  }, [refreshApiKey, refreshSync, refreshFacets, refreshCredits]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    if (!syncState.running) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    pollRef.current = setInterval(async () => {
      const s = await refreshSync();
      if (s && !s.running) {
        loadRows();
        refreshFacets();
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 5000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [syncState.running, refreshSync, loadRows, refreshFacets]);

  const onRunSync = async () => {
    if (!apiKeyState.connected) {
      setApiKeyOpen(true);
      toast.error("Connect ElevenLabs API key first");
      return;
    }
    try {
      await startSync();
      toast.success("Sync started");
      setSyncState((s) => ({ ...s, running: true }));
      refreshSync();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Sync failed to start";
      toast.error(msg);
    }
  };

  const onLogout = () => {
    clearStoredPasscode();
    navigate("/");
  };

  const onExportAll = () => {
    window.location.href = buildCsvUrl({});
  };
  const onExportFiltered = () => {
    window.location.href = buildCsvUrl(filterParams);
  };
  const onExportSelected = () => {
    if (selectedIds.size === 0) {
      toast.error("Select at least one conversation");
      return;
    }
    window.location.href = buildCsvUrl({ ids: Array.from(selectedIds).join(",") });
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === rows.length) return new Set();
      return new Set(rows.map((r) => r.external_id));
    });
  };

  const applyFilters = (f) => {
    setPage(1);
    setFilters(f);
  };

  const openUserSessions = (phone) => {
    setActiveTab("conversations");
    applyFilters({ ...emptyFilters, phone });
  };

  const openUserDrawer = (user) => {
    setSelectedUser(user);
  };

  const viewAllUserConversations = (phone) => {
    setSelectedUser(null);
    openUserSessions(phone);
  };

  return (
    <div className="min-h-screen bg-white text-black flex flex-col" data-testid="dashboard-page">
      <TopBar
        apiKeyState={apiKeyState}
        syncState={syncState}
        creditStats={creditStats}
        onConnectApi={() => setApiKeyOpen(true)}
        onSync={onRunSync}
        onExportAll={onExportAll}
        onExportFiltered={onExportFiltered}
        onExportSelected={onExportSelected}
        onOpenLogs={() => setLogsOpen(true)}
        onOpenMobileFilters={() => setMobileFiltersOpen(true)}
        onLogout={onLogout}
        selectedCount={selectedIds.size}
        filterActive={Object.values(filterParams).some((v) => v !== "" && v !== null && v !== undefined)}
      />

      {/* Tab bar */}
      <div className="border-t border-black/10 bg-white px-5 flex items-center gap-1" data-testid="dashboard-tabs">
        {[
          { key: "conversations", label: "Conversations" },
          { key: "users", label: "User Database" },
        ].map((t) => {
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setActiveTab(t.key)}
              className={`relative py-3 px-4 font-mono-plex text-[11px] uppercase tracking-[0.2em] transition-colors ${
                active ? "text-black" : "text-black/50 hover:text-black/80"
              }`}
            >
              {t.label}
              {active && (
                <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-black" />
              )}
            </button>
          );
        })}
        {activeTab === "conversations" && filters.phone && (
          <button
            data-testid="clear-phone-filter"
            onClick={() => applyFilters({ ...filters, phone: "" })}
            className="ml-2 inline-flex items-center gap-1 px-2 py-1 font-mono-plex text-[10px] uppercase tracking-[0.15em] border border-black/15 text-black/70 hover:text-black hover:border-black/40"
          >
            phone: {filters.phone} · clear
          </button>
        )}
      </div>

      {activeTab === "users" ? (
        <div className="flex-1 border-t border-black/10 overflow-hidden">
          <UsersTable onOpenUser={openUserDrawer} />
        </div>
      ) : (
        <div className="flex-1 grid grid-cols-1 md:grid-cols-[280px_1fr] border-t border-black/10">
          {/* Desktop sidebar */}
          <div className="hidden md:block">
            <FiltersSidebar
              filters={filters}
              setFilters={applyFilters}
              facets={facets}
              onClear={() => applyFilters(emptyFilters)}
            />
          </div>

          <ConversationTable
            rows={rows}
            loading={loading}
            total={total}
            page={page}
            pageSize={pageSize}
            setPage={setPage}
            onRowClick={(row) => setSelected(row)}
            selectedIds={selectedIds}
            toggleSelect={toggleSelect}
            toggleSelectAll={toggleSelectAll}
          />
        </div>
      )}

      {/* Mobile filters sheet */}
      <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
        <SheetContent
          side="left"
          data-testid="mobile-filters-sheet"
          className="w-[85vw] max-w-[320px] bg-white border-r border-black/15 text-black p-0"
        >
          <SheetTitle className="sr-only">Filters</SheetTitle>
          <SheetDescription className="sr-only">
            Adjust filters to narrow down conversations by search, month, archive
            state, date range, duration, status, call outcome, or environment.
          </SheetDescription>
          <div className="h-full overflow-y-auto">
            <FiltersSidebar
              filters={filters}
              setFilters={applyFilters}
              facets={facets}
              onClear={() => applyFilters(emptyFilters)}
            />
          </div>
        </SheetContent>
      </Sheet>

      <DetailDrawer
        open={Boolean(selected)}
        onOpenChange={(v) => !v && setSelected(null)}
        conversationSummary={selected}
      />

      <UserDetailDrawer
        open={Boolean(selectedUser)}
        onOpenChange={(v) => !v && setSelectedUser(null)}
        user={selectedUser}
        onOpenConversation={(row) => {
          setSelectedUser(null);
          setSelected(row);
        }}
        onViewAllInConversations={viewAllUserConversations}
      />

      <ApiKeyDialog
        open={apiKeyOpen}
        onOpenChange={setApiKeyOpen}
        apiKeyState={apiKeyState}
        onSaved={() => { refreshApiKey(); refreshCredits(); loadRows(); }}
      />

      <SyncLogsDialog open={logsOpen} onOpenChange={setLogsOpen} />
    </div>
  );
}
